// Apolo IDE — Build sprint directive from canvas (sprint plan-tab-v1, E7).
//
// Pega blocks + connections + plan e retorna UM markdown no formato
// sprint directive canônico (mesma estrutura do skill sprint-directive-
// prompt). Ordem topológica baseada em connections quando disponível;
// fallback por (y asc, x asc).

import { serializeChartToMarkdown } from './chartData.js';

const PHASE_HEADER = {
  discovery:    '## 🔍 Discovery',
  architecture: '## 🏗 Architecture',
  database:     '## 🗃 Database',
  delivery:     '## 🚀 Delivery',
  ui:           '## 🎨 UI',
  ux:           '## 👁 UX',
  security:     '## 🔒 Security',
  qa:           '## 🧪 QA',
};

function topologicalOrder(blocks, connections) {
  if (!Array.isArray(connections) || connections.length === 0) return null;
  const ids = new Set(blocks.map((b) => b.id));
  const adj = new Map(); // from -> [to]
  const indeg = new Map();
  for (const b of blocks) { adj.set(b.id, []); indeg.set(b.id, 0); }
  for (const c of connections) {
    if (!ids.has(c.from_block_id) || !ids.has(c.to_block_id)) continue;
    adj.get(c.from_block_id).push(c.to_block_id);
    indeg.set(c.to_block_id, (indeg.get(c.to_block_id) || 0) + 1);
  }
  // Kahn's algorithm
  const queue = blocks.filter((b) => (indeg.get(b.id) || 0) === 0).map((b) => b.id);
  const order = [];
  while (queue.length) {
    const id = queue.shift();
    order.push(id);
    for (const nb of (adj.get(id) || [])) {
      indeg.set(nb, indeg.get(nb) - 1);
      if (indeg.get(nb) === 0) queue.push(nb);
    }
  }
  if (order.length !== blocks.length) return null; // cycle => fallback
  const byId = Object.fromEntries(blocks.map((b) => [b.id, b]));
  return order.map((id) => byId[id]);
}

function spatialOrder(blocks) {
  return [...blocks].sort((a, b) => {
    if (a.y !== b.y) return a.y - b.y;
    return a.x - b.x;
  });
}

function renderBlock(b) {
  const payload = b.payload || {};
  switch (b.kind) {
    case 'heading': {
      const lvl = Math.max(1, Math.min(3, payload.level || 1));
      return `${'#'.repeat(lvl + 1)} ${payload.text || '(sem título)'}`;
    }
    case 'text':
      return payload.body || '_(texto vazio)_';
    case 'sticky':
      return `> 📝 **Nota:** ${payload.body || '_(vazio)_'}`;
    case 'todo': {
      const items = Array.isArray(payload.items) ? payload.items : [];
      if (items.length === 0) return '_(sem tarefas)_';
      return items.map((it) => `- [${it.done ? 'x' : ' '}] ${it.text || ''}`).join('\n');
    }
    case 'image':
      return payload.url ? `![${payload.alt || ''}](${payload.url})` : '_(imagem sem url)_';
    case 'video':
      return payload.url ? `🎬 [Vídeo](${payload.url})` : '_(vídeo sem url)_';
    case 'link':
      return payload.url ? `[${payload.title || payload.url}](${payload.url})` : '_(link sem url)_';
    case 'file':
      return `📎 **Arquivo:** ${payload.name || 'sem nome'}${payload.file_id ? ` (id: ${payload.file_id})` : ''}`;
    case 'pdf':
      return `📄 **PDF:** ${payload.name || 'sem nome'}${payload.file_id ? ` (id: ${payload.file_id})` : ''}`;
    case 'skill': {
      const name = payload.skill_name || 'unknown-skill';
      const ver = payload.version || 'latest';
      const notes = payload.notes ? `\n  ${payload.notes.replace(/\n/g, '\n  ')}` : '';
      return ['```skill name="' + name + '" version="' + ver + '"', notes.trim() || `descrição opcional da skill ${name}`, '```'].join('\n');
    }
    case 'divider':
      return payload.label ? `---\n\n**${payload.label.toUpperCase()}**\n\n---` : '---';
    case 'mermaid':
      return ['```mermaid', payload.source || 'graph TD;\n  A-->B;', '```'].join('\n');
    case 'table': {
      const headers = Array.isArray(payload.headers) ? payload.headers : [];
      const rows = Array.isArray(payload.rows) ? payload.rows : [];
      if (headers.length === 0) return '_(tabela vazia)_';
      const sep = headers.map(() => '---').join(' | ');
      const head = '| ' + headers.join(' | ') + ' |';
      const sepLine = '| ' + sep + ' |';
      const bodyLines = rows.map((r) => '| ' + r.map((c) => String(c || '').replace(/\|/g, '\\|')).join(' | ') + ' |');
      return [head, sepLine, ...bodyLines].join('\n');
    }
    case 'chat-ref':
      return payload.snippet ? `> 💬 ${payload.snippet}` : '_(trecho de conversa vazio)_';
    case 'embed':
      return payload.url ? `🔗 Embed: [${payload.url}](${payload.url})` : '_(embed sem url)_';
    default:
      return `_(bloco ${b.kind} sem renderer)_`;
  }
}

export function buildSprintDirective({ plan, blocks, connections, options = {} }) {
  if (!plan) return '';
  const ordered = topologicalOrder(blocks, connections) || spatialOrder(blocks);
  const dateIso = new Date().toISOString().slice(0, 10);
  const skillSet = new Set();
  for (const b of blocks) {
    if (b.kind === 'skill' && b.payload?.skill_name) skillSet.add(b.payload.skill_name);
  }
  const branchSlug = (options.branchSlug || plan.name || 'plan').toString().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 48);

  const lines = [];
  lines.push(`# ${plan.name} — Sprint Directive`);
  lines.push('');
  lines.push(`> **Generated by Apolo Plan tab** · ${dateIso}`);
  lines.push(`> Plan id: \`${plan.id}\`  ·  blocks: ${blocks.length}  ·  connections: ${connections?.length || 0}`);
  lines.push('');
  lines.push('## 🚫 NO-QUESTIONS DIRECTIVE');
  lines.push('Esta é uma execução autônoma. Não pause pra perguntar. Resolva ambiguidades via convenções do repo e documente decisões em `DECISIONS.md`.');
  lines.push('');
  lines.push('## 🧠 ANTI-HALLUCINATION DIRECTIVE');
  lines.push('Nada de phantom files/APIs/packages/types/TODO. Verifique imports antes de escrever. Rode testes ao fim de cada workstream.');
  lines.push('');
  lines.push('## 🌿 GITHUB FLOW');
  lines.push(`- Parent branch: \`feat/${branchSlug}\``);
  lines.push('- Sub-branches: `feat/<slug>--<epic>-<short>` (padrão Apolo `--`)');
  lines.push('- Commits conventional PT-BR (feat:/fix:/refactor:/test:/docs:)');
  lines.push('');
  if (skillSet.size > 0) {
    lines.push('## 🔧 SKILLS REQUERIDAS');
    for (const s of Array.from(skillSet).sort()) lines.push(`- \`/${s}\``);
    lines.push('');
  }
  // Estimativas por fase (tempo + complexidade + ROI + KPIs), se fornecidas.
  const est = options.estimates;
  if (est && Array.isArray(est.phases) && est.phases.length > 0) {
    lines.push('## 📊 ESTIMATIVAS POR FASE');
    lines.push('');
    lines.push('| Fase | Tempo | Complexidade | ROI | KPIs |');
    lines.push('| --- | --- | --- | --- | --- |');
    for (const p of est.phases) {
      const kpis = Array.isArray(p.kpis) ? p.kpis.join('; ') : '';
      const cell = (v) => String(v ?? '').replace(/\|/g, '\\|').replace(/\n/g, ' ');
      lines.push(`| ${cell(`${p.emoji || ''} ${p.label}`)} | ${cell(`${p.hours}h`)} | ${cell(p.complexity)} | ${cell(p.roi)} | ${cell(kpis)} |`);
    }
    lines.push('');
    lines.push(`**Total estimado:** ${est.totalHours}h`);
    lines.push('');
  }
  // Gráficos: cada bloco chart vira legenda + tabela de dados (a IA lê os números).
  // Considera qualquer série populada (não só a primeira).
  const hasChartData = (b) => b.kind === 'chart'
    && Array.isArray(b.payload?.series)
    && b.payload.series.some((s) => Array.isArray(s?.data) && s.data.length > 0);
  const charts = ordered.filter(hasChartData);
  if (charts.length > 0) {
    lines.push('## 📈 GRÁFICOS');
    lines.push('');
    for (const b of charts) {
      lines.push(serializeChartToMarkdown(b.payload));
      lines.push('');
    }
  }
  lines.push('## 📦 BLOCOS DO CANVAS (ordem topológica)');
  lines.push('');
  // Group blocks by skill phase if metadata present.
  const phaseGroups = new Map();
  const noPhase = [];
  for (const b of ordered) {
    if (b.kind === 'chart') continue; // já renderizado em "## 📈 GRÁFICOS"
    const ph = b.payload?._phase;
    if (ph && PHASE_HEADER[ph]) {
      if (!phaseGroups.has(ph)) phaseGroups.set(ph, []);
      phaseGroups.get(ph).push(b);
    } else {
      noPhase.push(b);
    }
  }
  if (phaseGroups.size > 0) {
    for (const [phase, list] of phaseGroups) {
      lines.push(PHASE_HEADER[phase]);
      lines.push('');
      for (const b of list) {
        lines.push(renderBlock(b));
        lines.push('');
      }
    }
  }
  if (noPhase.length > 0) {
    if (phaseGroups.size > 0) {
      lines.push('## 📝 Outros blocos');
      lines.push('');
    }
    for (const b of noPhase) {
      lines.push(renderBlock(b));
      lines.push('');
    }
  }
  lines.push('## ✅ FINAL DELIVERY REQUIREMENTS');
  lines.push('- Zero lint warnings nos arquivos modificados');
  lines.push('- Todos os testes passando');
  lines.push('- Sem TODO/FIXME pendentes');
  lines.push('- Dark + light parity');
  lines.push('- Mobile responsive');
  lines.push('- Acessibilidade WCAG 2.2 AA mínima');
  lines.push('- Commits conventional + PR final aberto');
  lines.push('');
  // Footer with SHA preview (computed by caller if needed)
  lines.push('---');
  lines.push(`_Exportado de Apolo Plan tab · plan \`${plan.id}\` · ${blocks.length} blocos_`);
  return lines.join('\n');
}

/** Convenience computer of a short summary for description. */
export function buildSprintSummary({ plan, blocks }) {
  const skills = new Set();
  let stickyCount = 0, textCount = 0, mediaCount = 0;
  for (const b of blocks) {
    if (b.kind === 'skill' && b.payload?.skill_name) skills.add(b.payload.skill_name);
    if (b.kind === 'sticky') stickyCount++;
    if (b.kind === 'text' || b.kind === 'heading') textCount++;
    if (b.kind === 'image' || b.kind === 'video' || b.kind === 'pdf' || b.kind === 'file') mediaCount++;
  }
  return [
    `Sprint gerada do Apolo Plan tab a partir do plano "${plan?.name}".`,
    `${blocks.length} blocos no canvas — ${skills.size} skills, ${textCount} textos/títulos, ${stickyCount} stickies, ${mediaCount} mídia/arquivos.`,
    skills.size > 0 ? `Skills: ${Array.from(skills).slice(0, 8).join(', ')}${skills.size > 8 ? '…' : ''}.` : '',
  ].filter(Boolean).join(' ');
}
